import {
  AUTH_LOADING_LABELS,
  SIGN_IN_PRODUCT,
  SIGN_IN_VIEW,
  type SignInView,
} from 'common/constants/auth';
import { FIRM_LIST_STATUSES } from 'common/types/firm';
import { AuthSelectionOption } from 'pages/Auth/components';
import type { ProductAccess } from 'pages/Auth/utils';
import { isEmailLike, isEmailLikeIdentifierReady, resolvePostAuthView } from 'pages/Auth/utils';
import { extractServerErrorMessage } from 'utilities/extractServerErrorMessage';
import { SIGN_IN_PASSWORD_INVALID_ERROR } from './SignIn.constants';
import { SIGN_IN_TEST_IDS } from './SignIn.testIds';
import type {
  CompletedTwoFactorSignInViewParams,
  SignInAuthRequirementsPendingParams,
  SignInPostAuthResolutionLoadingParams,
  SignInSessionStepLoadingParams,
} from './SignIn.types';

export { isEmailLike };

export const isSignInIdentifierReady = isEmailLikeIdentifierReady;

export const isSignInPasswordReady = (value: string) => value.length > 0;

export const signInViewRequiresIdentifier = (view: SignInView) =>
  view === SIGN_IN_VIEW.PASSWORD;

export const signInViewRequiresSession = (view: SignInView) =>
  view === SIGN_IN_VIEW.TWO_FACTOR
  || view === SIGN_IN_VIEW.TERMS
  || view === SIGN_IN_VIEW.PRODUCT_SELECTION
  || view === SIGN_IN_VIEW.WORKSPACE_SELECTION;

export const shouldShowSignInSessionStepLoading = ({
  hasCurrentUser,
  hasProductAccess,
  view,
}: SignInSessionStepLoadingParams) =>
  signInViewRequiresSession(view)
  && (!hasCurrentUser || (view === SIGN_IN_VIEW.PRODUCT_SELECTION && !hasProductAccess));

export const areSignInAuthRequirementsPending = ({
  firmListStatus,
  hasCurrentUser,
  isLoading,
}: SignInAuthRequirementsPendingParams) =>
  hasCurrentUser
  && (isLoading || firmListStatus === FIRM_LIST_STATUSES.IDLE);

export const shouldShowSignInPostAuthResolutionLoading = ({
  resolvedView,
  view,
}: SignInPostAuthResolutionLoadingParams) =>
  (view === SIGN_IN_VIEW.PRODUCT_SELECTION || view === SIGN_IN_VIEW.WORKSPACE_SELECTION)
  && Boolean(resolvedView)
  && resolvedView !== view;

export const resolveCompletedTwoFactorSignInView = ({
  firmListCount,
  requiresTerms,
  requiresTwoFactor,
  resolvedProduct,
  user,
}: CompletedTwoFactorSignInViewParams): SignInView =>
  resolvePostAuthView({
    firmListCount,
    isOtpVerified: true,
    product: resolvedProduct,
    requiresTerms,
    requiresTwoFactor,
    user,
    userHasTwoFactor: true,
  }) ?? SIGN_IN_VIEW.WORKSPACE_SELECTION;

export const isServiceError = (value: unknown): value is { status?: number; statusCode?: number } => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return 'status' in value || 'statusCode' in value;
};

export const isInvalidCredentialsError = (value: unknown) => {
  const serverMessage = extractServerErrorMessage(value)?.trim().toLowerCase();
  const invalidCredentialsMessage = SIGN_IN_PASSWORD_INVALID_ERROR.trim().toLowerCase();

  if (serverMessage === invalidCredentialsMessage) {
    return true;
  }

  if (!isServiceError(value)) {
    return false;
  }

  const statusCode = value.status ?? value.statusCode;
  return statusCode === 400 || statusCode === 401;
};

export const getSignInLoadingLabel = (view: SignInView) => {
  switch (view) {
    case SIGN_IN_VIEW.TWO_FACTOR:
      return AUTH_LOADING_LABELS.twoFactor;
    case SIGN_IN_VIEW.TERMS:
      return AUTH_LOADING_LABELS.accountRequirements;
    case SIGN_IN_VIEW.PRODUCT_SELECTION:
      return AUTH_LOADING_LABELS.productSelection;
    case SIGN_IN_VIEW.WORKSPACE_SELECTION:
      return AUTH_LOADING_LABELS.workspaceSelection;
    default:
      return AUTH_LOADING_LABELS.signInDefault;
  }
};

export const getSignInProductSelectionOptions = (productAccess: ProductAccess | null): AuthSelectionOption[] => {
  if (!productAccess) {
    return [];
  }

  const options: AuthSelectionOption[] = [];

  if (productAccess.hasPortfolioAccess) {
    options.push({
      description: 'Portfolio valuation, firms, funds, and company workflows.',
      testId: SIGN_IN_TEST_IDS.productPortfolioOption,
      title: 'Portfolio Valuation',
      value: SIGN_IN_PRODUCT.PORTFOLIO,
    });
  }

  if (productAccess.hasCompanyAccess) {
    options.push({
      description: 'Company valuations, dashboard access, and analyst workboard.',
      testId: SIGN_IN_TEST_IDS.productCompanyOption,
      title: 'Workboard',
      value: SIGN_IN_PRODUCT.COMPANY,
    });
  }

  return options;
};
